import {
  Component,
  computed,
  inject,
  LOCALE_ID,
  OnInit,
  output,
  signal,
  DestroyRef,
  input,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs';
import { ItemStatusTable } from '../item-status-table/item-status-table';
import { ItemStatusMode, LookupItem, NotFulfilledRow } from '../item-status-table/item-status-table.model';
import { ConfirmationDialog } from '../../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { OrderLookupService } from '../../../../../../services/orders/admin-order/order-lookup.service';
import { OrderPackageService } from '../../../../../../services/orders/admin-order/order-package.service';
import { CancelIcon } from '../../../../../../shared/ui/icons/cancel-icon/cancel-icon';
import { SubmitButton } from '../../../../../../shared/ui/Buttons/submit-button/submit-button';
import { PackagePayload } from '../package-order.model';
import { packageItemUpdated } from '../../../../../../store/order/package-order/package-item.actions';
import { Error } from '../../../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-item-status-popup',
  standalone: true,
  imports: [CommonModule, ItemStatusTable, ConfirmationDialog, CancelIcon, SubmitButton, Error],
  templateUrl: './item-status-popup.html',
})
export class ItemStatusPopup implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(OrderLookupService);
  private packageService = inject(OrderPackageService);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));
  itemType = input<'product' | 'offer' | 'bundle'>('product');
  itemId = input.required<string>();
  storeKey = input<string | null>(null);
  productTitleEn = input.required<string>();
  productTitleAr = input.required<string>();
  orderedQuantity = input.required<number>();
  mode = input<ItemStatusMode>('package');
  initialPackageQuantity = input<string | null>(null);
  initialNotPackageRows = input<any[]>([]);

  closed = output<void>();
  submitted = output<void>();

  fulfilledLabel = computed(() =>
    this.mode() === 'package' ? (this.isArabic() ? 'معبأ' : 'Packaged') : this.isArabic() ? 'تم الاستلام' : 'Delivered',
  );
  notFulfilledLabel = computed(() =>
    this.mode() === 'package'
      ? this.isArabic()
        ? 'غير معبأ'
        : 'Not Packaged'
      : this.isArabic()
        ? 'لم يتم الاستلام'
        : 'Not Delivered',
  );

  productTitle = computed(() => (this.isArabic() ? this.productTitleAr() : this.productTitleEn()));

  reasons = signal<LookupItem[]>([]);
  loadingReasons = signal(false);

  notFulfilledRows = signal<NotFulfilledRow[]>([]);

  showSubmitConfirm = signal(false);
  showCancelConfirm = signal(false);
  validationError = signal('');
  submitError = signal('');
  submitting = signal(false);

  constructor() {
    effect(() => {
      const initialRows = this.initialNotPackageRows();
      if (initialRows && initialRows.length > 0 && this.reasons().length > 0) {
        const mappedRows: NotFulfilledRow[] = initialRows.map((row, index) => ({
          rowId: row.rowId ?? Math.random().toString(36).substring(2, 9),
          quantity: Number(row.quantity),
          reasonId: row.reasonId ?? row.reason?.id ?? row.productReason?.reason?.id ?? null,
          otherReason: row.otherReason ?? row.productReason?.otherReason ?? '',
        }));
        this.notFulfilledRows.set(mappedRows);
      }
    });
  }

  ngOnInit() {
    this.loadingReasons.set(true);
    const request$ =
      this.mode() === 'package'
        ? this.lookupService.getPartiallyPackageReasons()
        : this.lookupService.getUndeliveryReasons();

    request$.pipe(finalize(() => this.loadingReasons.set(false))).subscribe({
      next: (res) => {
        this.reasons.set(res.list ?? []);
      },
      error: () => this.reasons.set([]),
    });
  }

  onRowsChange(rows: NotFulfilledRow[]) {
    this.notFulfilledRows.set(rows);
    this.showSubmitConfirm.set(false);
  }

  private validate(): boolean {
    for (const row of this.notFulfilledRows()) {
      if (row.reasonId == null) {
        this.validationError.set(this.isArabic() ? 'برجاء اختيار سبب لكل صف' : 'Please select a reason for every row');
        return false;
      }
      const otherId = this.reasons().find((r) => r.titleEn?.trim().toLowerCase() === 'other')?.id;
      if (row.reasonId === otherId && !row.otherReason.trim()) {
        this.validationError.set(
          this.isArabic() ? 'برجاء كتابة السبب في خانة أخرى' : 'Please fill in the Other reason',
        );
        return false;
      }
    }
    this.validationError.set('');
    return true;
  }

  onSubmitClick() {
    if (!this.validate()) return;

    if (!this.showSubmitConfirm()) {
      this.showSubmitConfirm.set(true);
      return;
    }

    if (this.mode() === 'package') {
      this.submitPackage();
    } else {
      this.submitted.emit();
      this.closed.emit();
    }
  }

  private submitPackage() {
    const orderedQuantity = this.orderedQuantity();
    const rows = this.notFulfilledRows();
    const fulfilledQty = Math.max(0, orderedQuantity - rows.reduce((acc, r) => acc + r.quantity, 0));
    const otherId = this.reasons().find((r) => r.titleEn?.trim().toLowerCase() === 'other')?.id;

    const payload: PackagePayload = {
      type: this.itemType(),
      package: { quantity: fulfilledQty },
      notPackage: rows.map((r) => {
        const isOther = r.reasonId === otherId;
        return {
          quantity: r.quantity,
          reason: {
            reasonId: r.reasonId as number,
            otherReason: isOther ? r.otherReason : undefined,
          },
        };
      }),
    };

    this.submitting.set(true);
    this.submitError.set('');

    const subscription = this.packageService
      .packageOrderItem(+this.itemId(), payload)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: () => {
          const notPackageRows = rows.map((r) => {
            const reason = this.reasons().find((rs) => rs.id === r.reasonId);
            return {
              quantity: r.quantity.toString(),
              productReason: {
                reason: { id: r.reasonId, titleEn: reason?.titleEn ?? '', titleAr: reason?.titleAr ?? '' },
                otherReason: r.otherReason || undefined,
              },
            };
          });

          this.store.dispatch(
            packageItemUpdated({
              itemId: this.storeKey() ?? this.itemId(),
              packageQuantity: fulfilledQty.toString(),
              notPackageRows,
            }),
          );

          this.submitted.emit();
          this.closed.emit();
        },
        error: (err) => {
          this.submitError.set(
            this.isArabic() ? err?.error?.messageAr || 'حدث خطأ ما' : err?.error?.messageEn || 'Something went wrong',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onCancelClick() {
    this.showCancelConfirm.set(true);
  }

  onConfirmCancel() {
    this.showCancelConfirm.set(false);
    this.closed.emit();
  }
}
